/**
 * Step 1 of 2 — the order sheet: buyer details + the priced item, then
 * Razorpay Standard Checkout for a one-time order.
 *
 * Deliberately NOT the membership path (utils/razorpayCheckout.startPlanCheckout):
 * that one needs a signed-in user and a plan code and verifies against the
 * billing tables. This is a guest-capable one-off purchase, so it only
 * borrows the shared checkout.js loader.
 *
 * Order of operations (same rule as billing): the server authors the order —
 * it re-resolves the price for the visitor's region and never reads an
 * amount from us — Razorpay collects, and the server verifies the signature
 * and the payment before the intake link is released.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { apiService } from '../../../services/apiService';
import { loadRazorpay } from '../../../utils/razorpayCheckout';
import { money } from '../../../utils/money';
import { IprModal, ModalCloseVia } from './Modal';
import { CouponQuote, ipIntelApi, IpIntelConfig, OrderResponse, storedSource, VerifyResponse, visitorIds } from './api';
import { ipTrack, ipClip, OrderPosition } from './track';
import { CONTACT_EMAIL, EMAIL_RE, GSTIN_RE } from './content';
import { usdAlt } from './usd';

interface Props {
  config: IpIntelConfig | null;
  configState: 'loading' | 'ready' | 'error';
  onRetryConfig: () => void;
  position: OrderPosition;
  onClose: () => void;
  /** `result` is the verify answer ({auto_intake, email, …}) for callers that
   *  confirm in place (the in-platform tab) instead of going to the intake page. */
  onPaid: (intakeUrl: string, orderRef: string, result?: VerifyResponse) => void;
  /** Extra order attribution, merged into `source` (e.g. {surface: 'platform',
   *  entry: 'tab'} from inside the platform, plus tech_id for a known technology). */
  source?: Record<string, unknown>;
}

type Phase = 'details' | 'creating' | 'checkout' | 'verifying';

interface Details {
  name: string; email: string; phone: string; organisation: string; designation: string; gstin: string;
}
const EMPTY: Details = { name: '', email: '', phone: '', organisation: '', designation: '', gstin: '' };

const DRAFT_KEY = 'ipintel_order_details';
/** Order ref of a payment Razorpay reported but we could not confirm. Kept
 *  for the tab so closing and reopening the sheet cannot lead to paying twice. */
const UNCONFIRMED_KEY = 'ipintel_unconfirmed_ref';
const readUnconfirmed = (): string => { try { return sessionStorage.getItem(UNCONFIRMED_KEY) || ''; } catch { return ''; } };

const unconfirmedText = (ref: string, said = '') =>
  `${said}We could not confirm the payment on this page (order ${ref}). If you were charged, `
  + `you will receive an e-mail with your intake link shortly — or write to ${CONTACT_EMAIL} quoting that reference.`;

const loadDraft = (): Details => {
  try { return { ...EMPTY, ...JSON.parse(sessionStorage.getItem(DRAFT_KEY) || '{}') }; } catch { return EMPTY; }
};

const validate = (d: Details, needGstin: boolean): Partial<Record<keyof Details, string>> => {
  const e: Partial<Record<keyof Details, string>> = {};
  if (d.name.trim().length < 2) e.name = 'Please enter your full name.';
  if (!EMAIL_RE.test(d.email.trim())) e.email = 'Please enter a valid e-mail address.';
  const digits = d.phone.replace(/\D/g, '');
  if (!/^[+\d][\d\s\-()]*$/.test(d.phone.trim()) || digits.length < 7 || digits.length > 15) {
    e.phone = 'Please enter a valid phone number.';
  }
  if (!d.organisation.trim()) e.organisation = 'Please enter your organisation.';
  if (!d.designation.trim()) e.designation = 'Please enter your designation.';
  if (needGstin && d.gstin.trim() && !GSTIN_RE.test(d.gstin.trim().toUpperCase())) {
    e.gstin = 'A GSTIN is 15 characters, e.g. 27AAPFU0939F1ZV.';
  }
  return e;
};

export const PaymentSheet: React.FC<Props> = ({ config, configState, onRetryConfig, position, onClose, onPaid, source: sourceExtra }) => {
  const [d, setD] = useState<Details>(loadDraft);
  const [errors, setErrors] = useState<Partial<Record<keyof Details, string>>>({});
  const [phase, setPhase] = useState<Phase>('details');
  const [message, setMessage] = useState<{ kind: 'error' | 'info'; text: string } | null>(
    () => (readUnconfirmed() ? { kind: 'error', text: unconfirmedText(readUnconfirmed()) } : null));
  const [hp, setHp] = useState('');
  // Set when Razorpay reported success but our verify call did not confirm
  // it. The money may have moved, so paying again is blocked — the webhook
  // settles the order and the intake e-mail follows.
  const [unconfirmed, setUnconfirmed] = useState<boolean>(() => !!readUnconfirmed());
  const busyRef = useRef(false);
  // An order the visitor dismissed can be paid again as-is: Razorpay allows
  // several attempts per order, and re-creating one per click would fill the
  // admin list with abandoned "created" rows.
  const lastOrderRef = useRef<{ key: string; order: OrderResponse } | null>(null);
  const phaseRef = useRef<Phase>('details');
  phaseRef.current = phase;

  const needGstin = (config?.region?.code || '').toUpperCase() === 'IN' || (config?.currency || '').toUpperCase() === 'INR';
  const signedInId = apiService.getStoredUserId?.() || '';

  // Prefill from the signed-in profile, only into fields still empty.
  useEffect(() => {
    if (!signedInId) return;
    let live = true;
    apiService.getProfile(signedInId).then(res => {
      if (!live || !res.success || !res.data) return;
      const u: any = (res.data as any).user || {};
      const p: any = (res.data as any).profile || {};
      const name = [u.first_name, u.last_name].filter(Boolean).join(' ').trim();
      setD(prev => ({
        ...prev,
        name: prev.name || name,
        email: prev.email || u.email || '',
        phone: prev.phone || u.phone || '',
        organisation: prev.organisation || p.organisation_name || '',
        designation: prev.designation || p.designation || '',
      }));
    }).catch(() => { /* prefill is a convenience */ });
    return () => { live = false; };
  }, [signedInId]);

  // Keep a draft for the tab, so closing the sheet by mistake loses nothing.
  useEffect(() => {
    try { sessionStorage.setItem(DRAFT_KEY, JSON.stringify(d)); } catch { /* ignore */ }
  }, [d]);

  const set = (k: keyof Details) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = k === 'gstin' ? e.target.value.toUpperCase() : e.target.value;
    setD(prev => ({ ...prev, [k]: v }));
    if (errors[k]) setErrors(prev => ({ ...prev, [k]: undefined }));
  };

  const busy = phase !== 'details';
  const priced = configState === 'ready' && !!config?.available && !!config.display;
  // ---- coupon code (owner, 2026-09-29): display only; the order re-checks it ----
  const [coupon, setCoupon] = useState<CouponQuote | null>(null);
  const [couponOpen, setCouponOpen] = useState(false);
  const [couponInput, setCouponInput] = useState('');
  const [couponBusy, setCouponBusy] = useState(false);
  const [couponError, setCouponError] = useState('');
  const applyCoupon = async () => {
    const code = couponInput.trim().toUpperCase();
    if (!code || couponBusy) return;
    setCouponBusy(true); setCouponError('');
    const res = await ipIntelApi.previewCoupon(code);
    setCouponBusy(false);
    ipTrack('ipintel_coupon_apply', { position, ok: res.ok });
    if (!res.ok) { setCoupon(null); setCouponError(res.error || 'That code is not valid.'); return; }
    setCoupon(res.data);
    lastOrderRef.current = null;
  };
  const removeCoupon = () => { setCoupon(null); setCouponInput(''); setCouponError(''); lastOrderRef.current = null; };
  const shownTotal = coupon ? coupon.display.total : (priced ? config!.display!.total : '');
  // Priced but the gateway keys are missing on the server: show the price,
  // do not offer a Pay button that can only fail.
  const gatewayOff = priced && config!.payments_enabled === false;
  const ready = priced && !gatewayOff;

  const taxLine = useMemo(() => {
    if (!config || !config.available || !config.tax_minor) return null;
    const rate = config.display?.tax_rate
      || (config.tax_rate_pct != null ? `${Number(config.tax_rate_pct).toString()}%` : '');
    return {
      label: `${config.tax_label || 'Tax'}${rate ? ` (${rate})` : ''}`,
      amount: config.display?.tax || money(config.tax_minor ?? null, config.currency || 'INR'),
    };
  }, [config]);

  const close = (reason: 'cancel' | ModalCloseVia) => {
    if (busy) return;
    ipTrack('ipintel_payment_cancel', { via: reason, position });
    onClose();
  };

  const verify = async (resp: any, order: OrderResponse) => {
    setPhase('verifying');
    setMessage({ kind: 'info', text: 'Payment received — confirming it with the bank…' });
    const v = await ipIntelApi.verifyOrder({
      razorpay_order_id: resp?.razorpay_order_id || order.razorpay_order_id,
      razorpay_payment_id: resp?.razorpay_payment_id,
      razorpay_signature: resp?.razorpay_signature,
    });
    if (v.ok && (v.data?.intake_url || v.data?.auto_intake)) {
      ipTrack('ipintel_payment_verified', { order_ref: v.data.order_ref || order.order_ref });
      try { sessionStorage.removeItem(DRAFT_KEY); } catch { /* ignore */ }
      busyRef.current = false;
      lastOrderRef.current = null;
      onPaid(v.data.intake_url || '', v.data.order_ref || order.order_ref, v.data);
      return;
    }
    // The money may well have moved: the webhook will still mark the order
    // paid and e-mail the intake link, so say that rather than "failed".
    ipTrack('ipintel_checkout_failed', {
      order_ref: order.order_ref, code: 'verify_failed', description: ipClip(v.ok ? 'no intake_url' : v.error),
    });
    busyRef.current = false;
    setUnconfirmed(true);
    try { sessionStorage.setItem(UNCONFIRMED_KEY, order.order_ref); } catch { /* ignore */ }
    setPhase('details');
    // The server's words when it has them (e.g. "Your payment is safe — we
    // will e-mail you the intake link as soon as it is confirmed").
    const raw = !v.ok && v.status !== 0 ? (v.error || '').trim() : '';
    const said = raw ? `${raw}${/[.!?]$/.test(raw) ? '' : '.'} ` : '';
    setMessage({ kind: 'error', text: unconfirmedText(order.order_ref, said) });
  };

  const openCheckout = async (order: OrderResponse, details: Details) => {
    const ok = await loadRazorpay();
    if (!ok || !(window as any).Razorpay) {
      ipTrack('ipintel_checkout_failed', { order_ref: order.order_ref, code: 'script_load', description: 'checkout.js failed to load' });
      busyRef.current = false;
      setPhase('details');
      setMessage({ kind: 'error', text: 'Could not load the payment window — check your connection and try again.' });
      return;
    }
    const rz = new (window as any).Razorpay({
      key: order.key_id || config?.razorpay_key_id,
      order_id: order.razorpay_order_id,
      amount: order.amount,
      currency: order.currency,
      name: 'UniversityTech.io',
      description: `IP Intelligence Report · ${order.order_ref}`,
      prefill: {
        name: order.prefill?.name || details.name,
        email: order.prefill?.email || details.email,
        contact: order.prefill?.contact || details.phone,
      },
      notes: { product: 'ip_intel', order_ref: order.order_ref },
      theme: { color: '#886A44' },
      handler: (resp: any) => { void verify(resp, order); },
      modal: {
        ondismiss: () => {
          if (phaseRef.current !== 'checkout') return;
          ipTrack('ipintel_checkout_dismissed', { order_ref: order.order_ref });
          busyRef.current = false;
          setPhase('details');
          setMessage(m => m?.kind === 'error' ? m
            : { kind: 'info', text: 'Payment window closed — you have not been charged. You can try again whenever you are ready.' });
        },
      },
    });
    rz.on('payment.failed', (r: any) => {
      const e = r?.error || {};
      ipTrack('ipintel_checkout_failed', {
        order_ref: order.order_ref, code: ipClip(e.code, 60), description: ipClip(e.description),
        reason: ipClip(e.reason, 80), step: ipClip(e.step, 60),
      });
      // Razorpay keeps its sheet open for a retry; ours just notes what happened.
      setMessage({ kind: 'error', text: `${e.description || 'The payment did not go through'} — you have not been charged. You can retry in the payment window.` });
    });
    setPhase('checkout');
    rz.open();
    ipTrack('ipintel_checkout_opened', { order_ref: order.order_ref, amount: order.amount, currency: order.currency });
  };

  const pay = async () => {
    if (busyRef.current || !ready || unconfirmed) return;
    const errs = validate(d, needGstin);
    setErrors(errs);
    if (Object.keys(errs).length) {
      ipTrack('ipintel_payment_details_invalid', { position, fields: Object.keys(errs).join(',') });
      const first = Object.keys(errs)[0];
      document.getElementById(`ipr-pay-${first}`)?.focus();
      return;
    }
    busyRef.current = true;
    setMessage(null);
    const details: Details = {
      name: d.name.trim(), email: d.email.trim().toLowerCase(), phone: d.phone.trim(),
      organisation: d.organisation.trim(), designation: d.designation.trim(),
      gstin: needGstin ? d.gstin.trim().toUpperCase() : '',
    };
    ipTrack('ipintel_payment_details_submit', { position, has_gstin: !!details.gstin, signed_in: !!signedInId });

    const key = JSON.stringify({ ...details, coupon: coupon?.code || '' });
    let order = lastOrderRef.current?.key === key ? lastOrderRef.current.order : null;
    if (!order) {
      setPhase('creating');
      const res = await ipIntelApi.createOrder({
        ...details,
        gstin: details.gstin || undefined,
        user_id: signedInId || undefined,
        source: { ...storedSource(), ...visitorIds(), position, path: window.location.pathname, ...(sourceExtra || {}) },
        website: hp,
        coupon: coupon?.code || undefined,
      });
      if (!res.ok || !res.data?.razorpay_order_id) {
        ipTrack('ipintel_checkout_failed', { code: 'order_create', description: ipClip(res.ok ? 'no order id' : res.error), status: res.status, position });
        busyRef.current = false;
        setPhase('details');
        setMessage({ kind: 'error', text: res.ok ? 'Could not start the payment. Please try again.' : res.error });
        if (!res.ok && coupon && /code/i.test(res.error || '')) { setCoupon(null); setCouponError(res.error); }
        return;
      }
      order = res.data;
      lastOrderRef.current = { key, order };
      ipTrack('ipintel_order_created', { order_ref: order.order_ref, position, reused: false });
    } else {
      ipTrack('ipintel_order_created', { order_ref: order.order_ref, position, reused: true });
    }
    await openCheckout(order, details);
  };

  const field = (k: keyof Details, label: string, props: React.InputHTMLAttributes<HTMLInputElement>, optional?: boolean) => (
    <div>
      <label className="ipr-label" htmlFor={`ipr-pay-${k}`}>
        {label}{optional && <span className="ipr-opt"> (optional)</span>}
      </label>
      <input
        id={`ipr-pay-${k}`}
        className={`ipr-input${errors[k] ? ' is-invalid' : ''}`}
        value={d[k]}
        onChange={set(k)}
        disabled={busy}
        aria-invalid={!!errors[k]}
        aria-describedby={errors[k] ? `ipr-pay-${k}-err` : undefined}
        {...props}
      />
      {errors[k] && <div id={`ipr-pay-${k}-err`} className="ipr-field-error">{errors[k]}</div>}
    </div>
  );

  const turnaround = config?.turnaround_days || 7;
  const payLabel = phase === 'creating' ? 'Creating your order…'
    : phase === 'checkout' ? 'Waiting for payment…'
    : phase === 'verifying' ? 'Confirming payment…'
    : unconfirmed ? 'Payment awaiting confirmation'
    : gatewayOff ? 'Online payment unavailable'
    : ready ? `Pay ${shownTotal}` : 'Pay';

  return (
    <IprModal labelledBy="ipr-pay-title" onClose={via => close(via)} locked={busy} maxWidth={480} className="ipr-modal-pay">
      <div className="ipr-eyebrow" style={{ color: 'var(--ipr-primary-600)', marginBottom: 10 }}>Step 1 of 2 · payment</div>
      <div id="ipr-pay-title" style={{ fontFamily: 'var(--ipr-font-display)', fontSize: 22, fontWeight: 700, color: 'var(--ipr-heading)', marginBottom: 8 }}>
        Complete your payment
      </div>
      <p style={{ fontSize: 14, lineHeight: 1.6, color: 'var(--ipr-text)', margin: '0 0 20px' }}>
        The intake form opens once payment is confirmed — that's what starts your {turnaround}-day clock.
      </p>

      <form
        onSubmit={e => { e.preventDefault(); void pay(); }}
        noValidate
        style={{ display: 'flex', flexDirection: 'column', gap: 14, marginBottom: 20 }}
      >
        <div className="ipr-eyebrow" style={{ color: 'var(--ipr-muted)', fontSize: 11 }}>Your details</div>
        {field('name', 'Full name', { autoComplete: 'name', placeholder: 'Ada Lovelace' })}
        <div className="ipr-grid-2">
          {field('email', 'Email', { type: 'email', autoComplete: 'email', placeholder: 'you@company.com' })}
          {field('phone', 'Phone', { type: 'tel', autoComplete: 'tel', placeholder: '+91 98765 43210' })}
        </div>
        <div className="ipr-grid-2">
          {field('organisation', 'Organisation', { autoComplete: 'organization', placeholder: 'Company or institute' })}
          {field('designation', 'Designation', { autoComplete: 'organization-title', placeholder: 'e.g. Head of Tech Transfer' })}
        </div>
        {needGstin && field('gstin', 'GSTIN', { placeholder: 'For a GST invoice', maxLength: 15, autoComplete: 'off' }, true)}
        {/* Honeypot: invisible to people, irresistible to form-filling bots. */}
        <div className="ipr-hp" aria-hidden="true">
          <label htmlFor="ipr-pay-website">Website</label>
          <input id="ipr-pay-website" tabIndex={-1} autoComplete="off" value={hp} onChange={e => setHp(e.target.value)} />
        </div>
        <button type="submit" hidden aria-hidden="true" tabIndex={-1} />
      </form>

      {/* Item card — the design's, plus the tax and total lines. */}
      <div style={{ border: '1px solid var(--ipr-border)', borderRadius: 8, padding: '16px 18px', marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '6px 12px', flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontFamily: 'var(--ipr-font-display)', fontWeight: 700, fontSize: 15, color: 'var(--ipr-heading)' }}>IP Intelligence Report</div>
            <div style={{ fontSize: 13, color: 'var(--ipr-muted)' }}>One technology · {turnaround}-day turnaround</div>
          </div>
          <div style={{ fontFamily: 'var(--ipr-font-display)', fontWeight: 700, fontSize: 18, color: 'var(--ipr-heading)', whiteSpace: 'nowrap' }}>
            {configState === 'loading' ? <span className="ipr-skel" style={{ width: 90, height: 20 }} />
              : priced ? <>{config!.display!.price} <span style={{ fontSize: 12, fontWeight: 400, color: 'var(--ipr-muted)' }}>+{config!.tax_label || 'GST'}</span></>
              : '—'}
            {/* The USD figure, display only: the payment below is in INR. */}
            {priced && (
              <div style={{ fontFamily: 'inherit', fontSize: 12, fontWeight: 400, color: 'var(--ipr-muted)', textAlign: 'right' }}>
                {usdAlt(config)}
              </div>
            )}
          </div>
        </div>
        {priced && (taxLine || coupon) && (
          <div style={{ borderTop: '1px solid var(--ipr-border)', marginTop: 14, paddingTop: 12, display: 'flex', flexDirection: 'column', gap: 6, fontSize: 13 }}>
            {coupon && (
              <div style={{ display: 'flex', justifyContent: 'space-between', color: 'var(--ipr-primary-600)', fontWeight: 600 }}>
                <span>Code {coupon.code} <button type="button" onClick={removeCoupon} disabled={busy}
                  style={{ background: 'none', border: 'none', padding: 0, font: 'inherit', fontWeight: 400, textDecoration: 'underline', cursor: 'pointer', color: 'var(--ipr-muted)' }}>remove</button></span>
                <span>−{coupon.discount_display}</span>
              </div>
            )}
            {taxLine && (
              <div style={{ display: 'flex', justifyContent: 'space-between', color: 'var(--ipr-gray-300)' }}>
                <span>{taxLine.label}</span><span>{coupon ? coupon.display.tax : taxLine.amount}</span>
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'space-between', color: 'var(--ipr-heading)', fontWeight: 700, fontSize: 15 }}>
              <span>Total</span><span>{shownTotal}</span>
            </div>
          </div>
        )}
        {ready && !coupon && (couponOpen ? (
          <form onSubmit={e => { e.preventDefault(); void applyCoupon(); }} style={{ display: 'flex', gap: 6, marginTop: 12 }}>
            <input aria-label="Coupon code" placeholder="Coupon code" value={couponInput} maxLength={32}
              onChange={e => { setCouponInput(e.target.value.toUpperCase()); setCouponError(''); }}
              style={{ flex: 1, minWidth: 0, height: 36, padding: '0 10px', border: '1px solid var(--ipr-border)', borderRadius: 8, fontSize: 13 }} />
            <button type="submit" className="ipr-btn-secondary" disabled={couponBusy || !couponInput.trim()}
              style={{ height: 36, padding: '0 14px' }}>{couponBusy ? 'Checking…' : 'Apply'}</button>
          </form>
        ) : (
          <button type="button" onClick={() => { setCouponOpen(true); ipTrack('ipintel_coupon_open', { position }); }}
            style={{ marginTop: 10, background: 'none', border: 'none', padding: 0, font: 'inherit', fontSize: 12, textDecoration: 'underline', cursor: 'pointer', color: 'var(--ipr-primary-600)' }}>
            Have a coupon code?
          </button>
        ))}
        {couponError && <div role="alert" style={{ color: '#B3261E', fontSize: 12, marginTop: 6 }}>{couponError}</div>}
      </div>

      {gatewayOff && (
        <div className="ipr-alert ipr-alert-info" style={{ marginBottom: 14 }}>
          Online payment is switched off for the moment. Write to{' '}
          <a href={`mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent('IP Intelligence Report order')}`}
            onClick={() => ipTrack('ipintel_click_contact', { position: 'payment_sheet' })}
            style={{ color: 'inherit', fontWeight: 700, textDecoration: 'underline' }}>{CONTACT_EMAIL}</a>{' '}
          and we will send you a payment link.
        </div>
      )}
      {configState === 'ready' && config && !config.available && (
        <div className="ipr-alert ipr-alert-info" style={{ marginBottom: 14 }}>
          The report is not sold online in your region yet. Write to{' '}
          <a href={`mailto:${config.contact_email || CONTACT_EMAIL}?subject=${encodeURIComponent('IP Intelligence Report — pricing for my region')}`}
            onClick={() => ipTrack('ipintel_click_contact', { position: 'payment_sheet', region: null })}
            style={{ color: 'inherit', fontWeight: 700, textDecoration: 'underline' }}>{config.contact_email || CONTACT_EMAIL}</a>{' '}
          for a quote.
        </div>
      )}
      {configState === 'error' && (
        <div className="ipr-alert ipr-alert-error" style={{ marginBottom: 14 }}>
          We couldn't load the price just now.{' '}
          <button type="button" onClick={() => { ipTrack('ipintel_config_retry', { position: 'payment_sheet' }); onRetryConfig(); }} style={{ background: 'none', border: 'none', padding: 0, color: 'inherit', fontWeight: 700, textDecoration: 'underline', cursor: 'pointer' }}>Try again</button>
        </div>
      )}
      {message && (
        <div className={`ipr-alert ${message.kind === 'error' ? 'ipr-alert-error' : 'ipr-alert-info'}`} role={message.kind === 'error' ? 'alert' : 'status'} style={{ marginBottom: 14 }}>
          {message.text}
        </div>
      )}

      <button type="button" className="ipr-btn-primary" style={{ width: '100%' }} onClick={() => void pay()} disabled={!ready || busy || unconfirmed}>
        {busy && <span className="ipr-spin" aria-hidden="true" />}
        {payLabel}
      </button>
      <button type="button" className="ipr-btn-secondary" style={{ width: '100%', marginTop: 10, boxShadow: 'none' }} onClick={() => close('cancel')} disabled={busy}>
        Cancel
      </button>
      {unconfirmed && phase === 'details' && (
        <div style={{ fontSize: 12, color: 'var(--ipr-gray-300)', textAlign: 'center', marginTop: 12 }}>
          Sure you were not charged?{' '}
          <button type="button" onClick={() => {
            ipTrack('ipintel_payment_new_order', { position });
            try { sessionStorage.removeItem(UNCONFIRMED_KEY); } catch { /* ignore */ }
            lastOrderRef.current = null; setUnconfirmed(false); setMessage(null);
          }} style={{ background: 'none', border: 'none', padding: 0, color: 'var(--ipr-primary-600)', fontWeight: 600, textDecoration: 'underline', cursor: 'pointer', fontSize: 12 }}>
            Start a new order
          </button>
        </div>
      )}
      {phase === 'checkout' && (
        // Escape hatch: Razorpay always reports a close, but if its window
        // vanished without one (a UPI app switch that never came back) the
        // sheet would otherwise stay locked on "Waiting for payment…".
        <div style={{ fontSize: 12, color: 'var(--ipr-gray-300)', textAlign: 'center', marginTop: 12 }}>
          Payment window gone?{' '}
          <button type="button" onClick={() => {
            ipTrack('ipintel_checkout_dismissed', { order_ref: lastOrderRef.current?.order.order_ref, via: 'reset' });
            busyRef.current = false; setPhase('details');
          }} style={{ background: 'none', border: 'none', padding: 0, color: 'var(--ipr-primary-600)', fontWeight: 600, textDecoration: 'underline', cursor: 'pointer', fontSize: 12 }}>
            Start again
          </button>
        </div>
      )}
      <div style={{ fontSize: 12, color: 'var(--ipr-muted)', textAlign: 'center', marginTop: 16 }}>UPI · Cards · Net Banking</div>
    </IprModal>
  );
};
